import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Building2,
  Hospital,
  GraduationCap,
  Shield,
  Users,
  Layers,
  MapPinned,
  Landmark,
  BusFront,
  Trees,
} from 'lucide-react';

const ICONS = {
  Building2,
  Layers,
  Hospital,
  GraduationCap,
  Shield,
  Landmark,
  BusFront,
  Trees,
};

const defaultContent = {
  badge: 'Members • Eligible • Affiliates',
  heading: 'Our Public Sector Coverage',
  intro:
    'PEA PNG brings public employees together across key sectors—supporting workplace fairness, professional standards, and stronger representation.',
  tiles: {
    sector: {
      title: 'Sector',
      value: 'Public Services',
      caption: 'Featured coverage',
    },
    reach: {
      title: 'Reach',
      value: 'Nationwide',
      caption: 'Across Papua New Guinea',
    },
    focus: {
      title: 'Focus',
      value: 'Protection',
      caption: 'Rights & representation',
    },
  },
  featured_label: 'Now featuring',
  slides: [
    {
      sector: 'Government Departments',
      status: 'Members & Eligible',
      description:
        'Representing public servants across national, provincial, and district administrations—working together for fair and safe workplaces.',
      image:
        'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1600&q=80',
      icon: 'Building2',
    },
    {
      sector: 'Statutory Bodies',
      status: 'Members & Eligible',
      description:
        'Supporting employees in authorities and commissions—strengthening professional standards, job security, and equitable conditions.',
      image:
        'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1600&q=80',
      icon: 'Layers',
    },
    {
      sector: 'Health',
      status: 'Members & Eligible',
      description:
        'Standing with health workers who serve communities every day—advocating for safer facilities, fair rostering, and stronger welfare.',
      image:
        'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1600&q=80',
      icon: 'Hospital',
    },
    {
      sector: 'Education',
      status: 'Members & Eligible',
      description:
        'Representing teachers and education professionals—promoting quality learning environments, fair pay, and career development.',
      image:
        'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1600&q=80',
      icon: 'GraduationCap',
    },
    {
      sector: 'Disciplinary Forces',
      status: 'Eligible / Affiliated',
      description:
        'Engaging with eligible and affiliated disciplinary forces—supporting respectful workplaces and strong, consistent employment standards.',
      image:
        'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1600&q=80',
      icon: 'Shield',
    },
    {
      sector: 'Local-Level Government & Municipal Services',
      status: 'Eligible',
      description:
        'Including municipal and local services that support communities—ensuring fair conditions and professional standards for public workers.',
      image:
        'https://images.unsplash.com/photo-1528909514045-2fa4ac7a08ba?w=1600&q=80',
      icon: 'Landmark',
    },
    {
      sector: 'Infrastructure, Works & Transport',
      status: 'Eligible',
      description:
        'Covering public works and transport services—supporting safe workplaces, proper resourcing, and consistent employment standards.',
      image:
        'https://images.unsplash.com/photo-1509395176047-4a66953fd231?w=1600&q=80',
      icon: 'BusFront',
    },
    {
      sector: 'Environment, Parks & Public Utilities',
      status: 'Eligible',
      description:
        'Supporting workers who maintain public spaces and essential services—promoting safety, respect, and fair employment conditions.',
      image:
        'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1600&q=80',
      icon: 'Trees',
    },
  ],
};

const CommitmentSlider = ({ content }) => {
  const merged = useMemo(() => ({ ...defaultContent, ...(content || {}) }), [content]);
  const slides = useMemo(() => merged.slides || defaultContent.slides, [merged.slides]);

  const [currentIndex, setCurrentIndex] = useState(0);

  // Pause auto-advance only after the user has actually moved their mouse over the carousel.
  const [isHovered, setIsHovered] = useState(false);
  const [hasMouseMovedOverCarousel, setHasMouseMovedOverCarousel] = useState(false);
  const isPaused = isHovered && hasMouseMovedOverCarousel;

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isPaused, slides.length]);

  useEffect(() => {
    // Keep index valid if slides array length changes via CMS
    if (currentIndex > slides.length - 1) setCurrentIndex(0);
  }, [currentIndex, slides.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  const currentSlide = slides[currentIndex];
  const CurrentIcon = ICONS[currentSlide.icon] || Building2;

  return (
    <section className="py-16 bg-white" aria-label={merged.heading || 'Our Public Sector Coverage'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left: PSI-inspired text + key figures */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold">
              <Users size={14} />
              {merged.badge}
            </div>

            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
              {merged.heading}
            </h2>
            <p className="mt-4 text-base text-gray-700 leading-relaxed">{merged.intro}</p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-gray-100 bg-gradient-to-br from-orange-50 to-orange-100 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">{merged.tiles?.sector?.title}</p>
                  <Layers className="text-orange-600" size={18} />
                </div>
                <p className="mt-2 text-2xl font-bold text-gray-900">{merged.tiles?.sector?.value}</p>
                <p className="mt-1 text-xs text-gray-600">{merged.tiles?.sector?.caption}</p>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-gradient-to-br from-blue-50 to-blue-100 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">{merged.tiles?.reach?.title}</p>
                  <MapPinned className="text-blue-600" size={18} />
                </div>
                <p className="mt-2 text-2xl font-bold text-gray-900">{merged.tiles?.reach?.value}</p>
                <p className="mt-1 text-xs text-gray-600">{merged.tiles?.reach?.caption}</p>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-gradient-to-br from-green-50 to-green-100 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">{merged.tiles?.focus?.title}</p>
                  <Shield className="text-green-700" size={18} />
                </div>
                <p className="mt-2 text-2xl font-bold text-gray-900">{merged.tiles?.focus?.value}</p>
                <p className="mt-1 text-xs text-gray-600">{merged.tiles?.focus?.caption}</p>
              </div>
            </div>

            <div className="mt-8 rounded-2xl bg-gray-50 border border-gray-100 p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {merged.featured_label}
              </p>
              <div className="mt-2 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
                  <CurrentIcon className="text-orange-600" size={20} />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">{currentSlide.sector}</p>
                  <p className="text-sm text-gray-600">{currentSlide.status}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Carousel (image + text overlay) */}
          <div className="lg:col-span-7">
            <div
              className="relative overflow-hidden rounded-3xl shadow-xl border border-gray-100"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => {
                setIsHovered(false);
                setHasMouseMovedOverCarousel(false);
              }}
              onMouseMove={() => setHasMouseMovedOverCarousel(true)}
            >
              <div className="relative h-[420px] sm:h-[460px]">
                {slides.map((slide, index) => {
                  const SlideIcon = ICONS[slide.icon] || Building2;
                  return (
                    <div
                      key={`${slide.sector}-${index}`}
                      className={`absolute inset-0 transition-opacity duration-700 ${
                        index === currentIndex ? 'opacity-100' : 'opacity-0'
                      }`}
                      aria-hidden={index !== currentIndex}
                    >
                      <img
                        src={slide.image}
                        alt={slide.sector}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/40 to-transparent" />

                      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 backdrop-blur flex items-center justify-center">
                            <SlideIcon className="text-white" size={22} />
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                                {slide.sector}
                              </h3>
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/15 text-white border border-white/20">
                                {slide.status}
                              </span>
                            </div>
                            <p className="mt-3 text-base text-white/90 leading-relaxed max-w-2xl">
                              {slide.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Arrows */}
                <button
                  type="button"
                  onClick={goToPrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur flex items-center justify-center transition z-10"
                  aria-label="Previous sector"
                >
                  <ChevronLeft className="text-white" size={22} />
                </button>
                <button
                  type="button"
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur flex items-center justify-center transition z-10"
                  aria-label="Next sector"
                >
                  <ChevronRight className="text-white" size={22} />
                </button>

                {/* Dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
                  {slides.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setCurrentIndex(idx)}
                      className={`h-2.5 rounded-full transition-all ${
                        idx === currentIndex
                          ? 'bg-white w-10'
                          : 'bg-white/50 hover:bg-white/70 w-2.5'
                      }`}
                      aria-label={`Go to sector ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500">Tip: Hover to pause the slideshow.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommitmentSlider;
export { ICONS as COVERAGE_ICON_MAP, defaultContent as defaultCoverageContent };
